import { infoBarAddHandler, infoBarRemoveHandler } from './infoBarHandlers';
import isSmimeEnabledInViewState from './isSmimeEnabledInViewState';
import setNumberOfWaitingAttachments from '../actions/setNumberOfWaitingAttachments';
import trySaveMessage, { waitForActiveSaving } from '../actions/trySaveMessage';
import ComposeViewState from '../store/schema/ComposeViewState';
import { AttachmentFile, AttachmentFileList } from 'owa-attachment-file-types';
import { ClientAttachmentId, getUserMailboxInfo } from 'owa-client-ids';
import { isFeatureEnabled } from 'owa-feature-flags';
import InfoBarMessageViewStateCreator from 'owa-info-bar/lib/schema/InfoBarMessageViewStateCreator';
import triggerPolicyTips from 'owa-mail-compose-store/lib/actions/triggerPolicyTips';
import { isPolicyTipsEnabled } from 'owa-policy-tips/lib/utils/isPolicyTipsEnabled';
import EventTrigger from 'owa-service/lib/contract/EventTrigger';
import { lazyCreateSmimeAttachmentsFromFiles } from 'owa-smime';
import { trace } from 'owa-trace';
import {
    lazyCreateAttachmentsViaQueueManager,
    CreateAttachmentsProperties,
} from 'owa-attachment-well-data';

function createAttachmentsHandler(
    files: AttachmentFileList,
    composeViewState: ComposeViewState,
    properties: CreateAttachmentsProperties,
    processInlineImageBeforeUploadFunc?: (temporaryId: string, file: AttachmentFile) => void,
    attachmentInfoBarAddHandler?: (
        messageId: string,
        infoBarMessageViewStateCreator: InfoBarMessageViewStateCreator
    ) => void,
    attachmentInfoBarRemoveHandler?: (infoBarMessageIds: string[]) => void,
    onFinish?: (attachmentId: ClientAttachmentId) => void
) {
    if (isSmimeEnabledInViewState(composeViewState)) {
        lazyCreateSmimeAttachmentsFromFiles.import().then(async createSmimeAttachmentsFromFiles => {
            try {
                const filesQueuedToAttach = await createSmimeAttachmentsFromFiles(
                    files,
                    { ...composeViewState.itemId, mailboxInfo: getUserMailboxInfo() },
                    composeViewState.attachmentWell,
                    properties
                );

                inlineAttachmentsHandler(
                    properties,
                    filesQueuedToAttach,
                    composeViewState,
                    processInlineImageBeforeUploadFunc
                );
            } catch (e) {
                trace.warn(e);
            }
        });
    } else {
        lazyCreateAttachmentsViaQueueManager.import().then(createAttachmentsViaQueueManager => {
            const filesQueuedToAttach = createAttachmentsViaQueueManager(
                composeViewState.attachmentWell,
                { ...composeViewState.itemId, mailboxInfo: getUserMailboxInfo() },
                files,
                attachmentInfoBarAddHandler
                    ? attachmentInfoBarAddHandler
                    : infoBarAddHandler(composeViewState),
                attachmentInfoBarRemoveHandler
                    ? attachmentInfoBarRemoveHandler
                    : infoBarRemoveHandler(composeViewState),
                properties,
                onFinish
            );

            inlineAttachmentsHandler(
                properties,
                filesQueuedToAttach,
                composeViewState,
                processInlineImageBeforeUploadFunc
            );
        });
    }
}

function inlineAttachmentsHandler(
    properties: CreateAttachmentsProperties,
    filesQueuedToAttach: AttachmentFileList,
    composeViewState: ComposeViewState,
    processInlineImageBeforeUploadFunc: (temporaryId: string, file: AttachmentFile) => void
) {
    if (properties.isInline) {
        const fileLength = filesQueuedToAttach.length;
        const inlineAttachmentsLength = composeViewState.attachmentWell.inlineAttachments.length;
        for (let i = 0; i < fileLength; i++) {
            // Get the last fileLength number of inline attachments
            // since they are the new ones created from above createAttachmentsViaQueueManager.
            const attachmentState =
                composeViewState.attachmentWell.inlineAttachments[
                    inlineAttachmentsLength - fileLength + i
                ];
            processInlineImageBeforeUploadFunc &&
                processInlineImageBeforeUploadFunc(
                    attachmentState.placeholderId,
                    filesQueuedToAttach[i]
                );
        }
    }
}

export default async function createAttachments(
    files: AttachmentFileList,
    composeViewState: ComposeViewState,
    properties: CreateAttachmentsProperties,
    processInlineImageBeforeUploadFunc: (temporaryId: string, file: AttachmentFile) => void,
    attachmentInfoBarAddHandler?: (
        messageId: string,
        infoBarMessageViewStateCreator: InfoBarMessageViewStateCreator
    ) => void,
    attachmentInfoBarRemoveHandler?: (infoBarMessageIds: string[]) => void,
    onFinish?: (attachmentId: ClientAttachmentId) => void
) {
    let attachmentIsWaiting = false;
    if (isFeatureEnabled('cmp-upgradeCompose') && !composeViewState.itemId) {
        // Until item id is retrieved, attachment creation is put on hold
        // and messages are prevented from being sent with a wait dialog.
        // This mimics the behavior in jsMVVM (VSO 33271)
        attachmentIsWaiting = true;
        setNumberOfWaitingAttachments(
            composeViewState,
            composeViewState.numberOfWaitingAttachments + 1
        );
    }

    try {
        if (!composeViewState.itemId && !(await waitForActiveSaving(composeViewState))) {
            await trySaveMessage(composeViewState);
        }

        createAttachmentsHandler(
            files,
            composeViewState,
            properties,
            processInlineImageBeforeUploadFunc,
            attachmentInfoBarAddHandler,
            attachmentInfoBarRemoveHandler,
            attachmentId => {
                if (onFinish) {
                    onFinish(attachmentId);
                }
                if (isPolicyTipsEnabled()) {
                    triggerPolicyTips(composeViewState, EventTrigger.AttachmentAdded);
                }
            }
        );
    } finally {
        if (attachmentIsWaiting) {
            // Always decrement the number of waiting attachments in the end,
            // even if the save failed and the attachment was not handled, so
            // that the user can still send the message
            setNumberOfWaitingAttachments(
                composeViewState,
                composeViewState.numberOfWaitingAttachments - 1
            );
        }
    }
}
